/**
 * Mirrors meeba_app/lib/core/utils/order_status_label.dart, provider viewer only.
 *
 * The business status deliberately stays DISCUSSION from inquiry through acceptance - only a
 * verified authorization confirms a booking - so showing it alone told the provider nothing
 * while their priced offer was sitting there. quoteStatus and paymentStatus already carry the
 * detail, and both are already on the DTO; OrdersManagement simply was not reading them.
 *
 * Precedence is deliberate: a terminal business status wins over the quote axis, so a cancelled
 * order never reads as "Proposal sent".
 */
export function orderStatusKey(order) {
  switch (order?.status) {
    case 'CANCELLED_BY_USER': return 'stateCancelledByCustomer';
    case 'CANCELLED_BY_PROVIDER': return 'stateCancelled';
    case 'CANCELLED_PAYMENT_FAILED': return 'statePaymentFailed';
    case 'COMPLETED': return 'stateCompleted';
    // Captured money reads as "Paid — mark complete when done": the provider's next step.
    case 'CONFIRMED':
      return order?.paymentStatus === 'CAPTURED' ? 'statePaid' : 'stateConfirmed';
    default: break;
  }

  switch (order?.quoteStatus) {
    case 'ACCEPTED':
      return order?.paymentStatus === 'AWAITING_AUTHORIZATION_WINDOW'
        ? 'statePaymentOpensSoon'
        : 'stateAwaitingPayment';
    case 'QUOTED': return 'stateProposalSent';
    case 'DECLINED': return 'stateProposalDeclined';
    case 'EXPIRED': return 'stateProposalExpired';
    default: return 'stateAwaitingYourProposal';
  }
}

/** The localized chip text. `t` is the react-i18next translator. */
export function orderStatusLabel(order, t) {
  return t(`orders.${orderStatusKey(order)}`);
}

/**
 * The MUI chip colour, from the SAME key as the text so the two cannot disagree.
 *
 * getStatusColor switched on the business axis alone, so every proposal state shared
 * DISCUSSION's one colour. Declined and expired are neutral rather than error: the quote ends
 * but the thread stays open and a fresh proposal restarts it.
 */
export function orderStatusColor(order) {
  switch (orderStatusKey(order)) {
    case 'stateCancelled':
    case 'stateCancelledByCustomer':
    case 'statePaymentFailed':
      return 'error';
    case 'stateCompleted':
    case 'statePaid':
      return 'success';
    case 'stateConfirmed':
      return 'primary';
    case 'statePaymentOpensSoon':
    case 'stateAwaitingPayment':
    case 'stateAwaitingYourProposal':
      return 'warning';
    case 'stateProposalSent':
      return 'info';
    case 'stateProposalDeclined':
    case 'stateProposalExpired':
    default:
      return 'default';
  }
}

// Completion is allowed server-side for a SETTLED booking: CAPTURED (money moved) or OFFLINE
// (settled outside the app). Mirrors PaymentStatus.SETTLED on the backend.
const SETTLED = ['CAPTURED', 'OFFLINE'];

/**
 * A booked service whose payment has not been captured yet.
 *
 * Completion requires a SETTLED state server-side (CAPTURED, or OFFLINE for a booking paid
 * outside the app) — COMPLETED is proof of both payment and delivery — so "Complete Job" is
 * shown disabled, with the reason, until the hold is charged instead of letting the provider
 * hit a 409.
 */
export function completeAwaitsCapture(order) {
  return (order?.orderType || 'SERVICE') === 'SERVICE' &&
    order?.status === 'CONFIRMED' &&
    !SETTLED.includes(order?.paymentStatus);
}

/** Provider completion is refused server-side before expectedCompletionAt, in either settlement mode. */
export function completeAwaitsEnd(order) {
  return (order?.orderType || 'SERVICE') === 'SERVICE' &&
    order?.status === 'CONFIRMED' &&
    !!order?.expectedCompletionAt && new Date(order.expectedCompletionAt).getTime() > Date.now();
}

const CANCELLED_STATUSES = ['CANCELLED_BY_USER', 'CANCELLED_BY_PROVIDER', 'CANCELLED_PAYMENT_FAILED'];

/**
 * Whether a cancel call actually finished the cancellation.
 *
 * The server answers 200 even when it only RECORDED the request: while a payment hold's state is
 * unknown it refuses to declare the booking cancelled until reconciliation confirms the hold is
 * released. Announcing "cancelled" on every 2xx told the provider something that was not true.
 * A response with no body is treated as done, as before.
 */
export function cancellationCompleted(updated) {
  if (!updated || !updated.status) return true;
  return CANCELLED_STATUSES.includes(updated.status);
}

// Same five as the backend's LEGACY_TERMINAL and the app's OrderRequest._terminalStatuses.
export const TERMINAL_STATUSES = [
  'COMPLETED', 'FINISHED', 'CANCELLED_BY_USER', 'CANCELLED_BY_PROVIDER', 'CANCELLED_PAYMENT_FAILED',
];

/**
 * No further booking actions (quote, complete, cancel). Distinct from the chat closing, which the
 * server decides and reports as `chatClosed` a few days after the order closes.
 */
export function isTerminal(order) {
  return TERMINAL_STATUSES.includes(order?.status);
}
